import { createBrowserRouter, Navigate } from 'react-router-dom';
import Routes from './Routes.ts';
import LoginPage from '@/pages/LoginPage.tsx';
import { transformRouterConfig } from '@/router/transformRouter.tsx';
import RegisterPage from '@/pages/RegisterPage.tsx';
import Default from '@/layouts/Default/Default.tsx';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage.tsx';
import ResetPasswordPage from '@/pages/ResetPasswordPage.tsx';
import HomePage from '@/pages/HomePage.tsx';
import SimpleCenter from '@/layouts/SimpleCenter/SimpleCenter.tsx';
import ConfirmEmailPage from '@/pages/ConfirmEmailPage.tsx';
import PaymentSelectPage from '@/pages/PaymentSelectPage.tsx';
import { LayoutInternal } from '$/views/emails/layout.tsx';
import BrandedSimple from '@/layouts/BrandedSimple/BrandedSimple.tsx';
import PasswordChangedConfirmationPage from '@/pages/PasswordChangedConfirmationPage.tsx';
import EmailChangedConfirmationPage from '@/pages/EmailChangedConfirmationPage.tsx';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage.tsx';
import TermsAndConditionsPage from '@/pages/TermsAndConditionsPage.tsx';
import OnboardingPage from '@/pages/OnboardingPage.tsx';
import StudentOnboardingPage from '@/pages/StudentOnboardingPage.tsx';
import PracticeArea from '@/pages/PracticeArea.tsx';
import FullPastPapersPage from '@/pages/FullPastPapersPage.tsx';
import DownloadPastPapersPage from '@/pages/DownloadPastPapersPage.tsx';
import ProgressTrackerPage from '@/pages/ProgressTrackerPage.tsx';
import AccountSettingsPage from '@/pages/AccountSettingsPage.tsx';
import TakePaperPage from '@/pages/TakePaperPage.tsx';
import AttemptResultsPage from '@/pages/AttemptResultsPage.tsx';
import TakePracticeSetPage from '@/pages/TakePracticeSetPage.tsx';
import PracticeSetResultsPage from '@/pages/PracticeSetResultsPage.tsx';
import PastPaperManagement from '@/pages/admin/PastPaperManagement.tsx';
import UploadPastPaperPage from '@/pages/admin/UploadPastPaperPage.tsx';
import EditPastPaperPage from '@/pages/admin/EditPastPaperPage.tsx';
import QuestionCategoriesPage from '@/pages/admin/QuestionCategoriesPage.tsx';
import EditQuestionCategoryPage from '@/pages/admin/EditQuestionCategoryPage.tsx';
import GradingPromptsPage from '@/pages/admin/GradingPromptsPage.tsx';
import EditGradingPromptPage from '@/pages/admin/EditGradingPromptPage.tsx';
import PracticeQuestionsPage from '@/pages/admin/PracticeQuestionsPage.tsx';
import EditPracticeQuestionPage from '@/pages/admin/EditPracticeQuestionPage.tsx';
import QuestionGenerationPromptsPage from '@/pages/admin/QuestionGenerationPromptsPage.tsx';
import ProtectedAdminRoute from '@/components/admin/ProtectedAdminRoute.tsx';

const router = createBrowserRouter(
  transformRouterConfig([
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.DASHBOARD,
          element: <HomePage />,
        },
        {
          path: Routes.PRACTICE_AREA,
          element: <PracticeArea />,
        },
        {
          path: Routes.PRACTICE_SET,
          element: <TakePracticeSetPage />,
        },
        {
          path: Routes.PRACTICE_SET_RESULTS,
          element: <PracticeSetResultsPage />,
        },
        {
          path: Routes.FULL_PAST_PAPERS,
          element: <FullPastPapersPage />,
        },
        {
          path: Routes.PAPER_ATTEMPT,
          element: <TakePaperPage />,
        },
        {
          path: Routes.ATTEMPT_RESULTS,
          element: <AttemptResultsPage />,
        },
        {
          path: Routes.DOWNLOAD_PAST_PAPERS,
          element: <DownloadPastPapersPage />,
        },
        {
          path: Routes.PROGRESS_TRACKER,
          element: <ProgressTrackerPage />,
        },
        {
          path: Routes.ACCOUNT_SETTINGS,
          element: <AccountSettingsPage />,
        },
        // {
        //   path: Routes.SANDBOX,
        //   element: <SandboxPage />,
        // },
        // {
        //   path: Routes.DATA_TABLE,
        //   element: <DataTablePage />,
        // },
        // {
        //   path: Routes.INPUTS,
        //   element: <InputsPage />,
        // },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['notLoggedIn'],
      children: [
        {
          path: Routes.FORGOT_PASSWORD,
          element: <ForgotPasswordPage />,
        },
        {
          path: Routes.LOGIN,
          element: <LoginPage />,
        },
        {
          path: Routes.REGISTER,
          element: <RegisterPage />,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <ConfirmEmailPage />,
        },
      ],
    },
    // SimpleCenter layout
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.RESET_PASSWORD,
          element: <ResetPasswordPage />,
        },
      ],
    },
    {
      path: '/',
      element: <BrandedSimple />,
      children: [
        {
          path: Routes.PASSWORD_CHANGED_CONFIRMATION,
          element: <PasswordChangedConfirmationPage />,
        },
        {
          path: Routes.EMAIL_CHANGED_CONFIRMATION,
          element: <EmailChangedConfirmationPage />,
        },
        {
          path: Routes.PRIVACY_POLICY,
          element: <PrivacyPolicyPage />,
        },
        {
          path: Routes.TERMS_AND_CONDITIONS,
          element: <TermsAndConditionsPage />,
        },
      ],
    },
    // SimpleCenter logged in
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.ONBOARDING,
          element: <OnboardingPage />,
        },
        {
          path: Routes.STUDENT_ONBOARDING,
          element: <StudentOnboardingPage />,
        },
        {
          path: Routes.PAYMENT_SELECT,
          element: <PaymentSelectPage />,
        },
      ],
    },
    // Admin routes (no layout wrapper - AdminLayout provides its own)
    {
      path: '/admin',
      element: <Default.Layout />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.ADMIN_PAST_PAPERS,
          element: (
            <ProtectedAdminRoute>
              <PastPaperManagement />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_UPLOAD_PAST_PAPER,
          element: (
            <ProtectedAdminRoute>
              <UploadPastPaperPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_EDIT_PAST_PAPER,
          element: (
            <ProtectedAdminRoute>
              <EditPastPaperPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_CATEGORIES,
          element: (
            <ProtectedAdminRoute>
              <QuestionCategoriesPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_EDIT_CATEGORY,
          element: (
            <ProtectedAdminRoute>
              <EditQuestionCategoryPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_GRADING_PROMPTS,
          element: (
            <ProtectedAdminRoute>
              <GradingPromptsPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_EDIT_GRADING_PROMPT,
          element: (
            <ProtectedAdminRoute>
              <EditGradingPromptPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_PRACTICE_QUESTIONS,
          element: (
            <ProtectedAdminRoute>
              <PracticeQuestionsPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_EDIT_PRACTICE_QUESTION,
          element: (
            <ProtectedAdminRoute>
              <EditPracticeQuestionPage />
            </ProtectedAdminRoute>
          ),
        },
        {
          path: Routes.ADMIN_QUESTION_GENERATION_PROMPTS,
          element: (
            <ProtectedAdminRoute>
              <QuestionGenerationPromptsPage />
            </ProtectedAdminRoute>
          ),
        },
      ],
    },
    {
      path: Routes.EMAIL_TEMPLATE,
      element: (
        <LayoutInternal
          appUrl={import.meta.env.VITE_APP_URL}
          appName={'pastpaperplus'}
          logo={`${import.meta.env.VITE_APP_URL}/assets/logo.png`}
        >
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab animi atque possimus, quaerat quasi tempore
          totam! Architecto assumenda cumque deleniti dolor expedita facilis illo minima nihil quidem soluta? Hic,
          laboriosam!
        </LayoutInternal>
      ),
    },
    {
      path: '*',
      element: <Navigate to={Routes.DASHBOARD} />,
    },
  ])
);

export default router;
